import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, ArrowLeft, Copy, Send } from 'lucide-react';
import { Button } from './ui/button';
import { trackUiError } from '../lib/observability';

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
  /** Area label for Clarity (e.g. 'app-root', 'dashboard'). */
  area?: string;
};

type State = {
  error: Error | null;
  info: ErrorInfo | null;
  errorId: string;
  showDetails: boolean;
  isReporting: boolean;
  reportSent: boolean;
};

function generateErrorId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `ERR-${timestamp}-${random}`.toUpperCase();
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    error: null,
    info: null,
    errorId: '',
    showDetails: false,
    isReporting: false,
    reportSent: false,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, errorId: generateErrorId() };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ info });

    // Clarity: record a SAFE signal only — error class + area, never message/stack.
    trackUiError({ area: this.props.area ?? 'app-root', errorName: error.name });

    // Log to console in dev
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary]', error, info);
    }

    // Call parent error handler if provided
    this.props.onError?.(error, info);

    // Send to error reporting endpoint in production
    if (import.meta.env.PROD) {
      this.reportError(error, info);
    }
  }

  private async reportError(error: Error, info: ErrorInfo) {
    try {
      await fetch('/api/public/metrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'client_error',
          errorId: this.state.errorId,
          message: error.message,
          stack: error.stack,
          componentStack: info.componentStack,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }),
      });
      this.setState({ reportSent: true });
    } catch {
      // Silently fail — error reporting should never break the app
    }
  }

  private handleRetry = () => {
    this.setState({ error: null, info: null, errorId: '', showDetails: false, reportSent: false });
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      this.handleGoHome();
    }
  };

  private handleCopyError = () => {
    const { error, info, errorId } = this.state;
    const text = [
      `Error ID: ${errorId}`,
      `Message: ${error?.message}`,
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      info?.componentStack ? `Stack: ${info.componentStack}` : '',
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(text).catch(() => {});
  };

  private handleReportManually = () => {
    this.setState({ isReporting: true });
    // Simulate report submission
    setTimeout(() => {
      this.setState({ isReporting: false, reportSent: true });
    }, 1000);
  };

  render() {
    const { error, info, errorId, showDetails, isReporting, reportSent } = this.state;
    const { children, fallback } = this.props;

    if (!error) return children;

    if (fallback) return fallback;

    return (
      <div className="flex min-h-[60vh] items-center justify-center p-4 md:p-6">
        <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900 p-6 md:p-8 text-center shadow-lg">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 dark:bg-red-950/30">
            <AlertTriangle className="h-6 w-6 text-red-500" aria-hidden="true" />
          </div>

          <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
            Something went wrong
          </h3>

          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            {error.message || 'An unexpected error occurred.'}
          </p>

          {errorId && (
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500 font-mono">
              Error ID: {errorId}
            </p>
          )}

          {reportSent && (
            <p className="mt-2 text-xs text-green-600 dark:text-green-400">
              Error report sent successfully.
            </p>
          )}

          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button onClick={this.handleRetry} variant="default" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Try Again
            </Button>
            <Button onClick={this.handleGoBack} variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Go Back
            </Button>
            <Button onClick={this.handleGoHome} variant="outline" className="gap-2">
              <Home className="h-4 w-4" />
              Home
            </Button>
          </div>

          <div className="mt-4 flex justify-center gap-3">
            <button
              onClick={this.handleCopyError}
              className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex items-center gap-1"
            >
              <Copy className="h-3 w-3" />
              Copy error
            </button>
            {!reportSent && (
              <button
                onClick={this.handleReportManually}
                disabled={isReporting}
                className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex items-center gap-1 disabled:opacity-50"
              >
                <Send className="h-3 w-3" />
                {isReporting ? 'Sending...' : 'Report issue'}
              </button>
            )}
          </div>

          <button
            onClick={() => this.setState({ showDetails: !showDetails })}
            className="mt-3 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            {showDetails ? 'Hide details' : 'Show details'}
          </button>

          {showDetails && info?.componentStack && (
            <pre className="mt-3 max-h-48 overflow-auto rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800 p-3 text-left text-[10px] text-slate-600 dark:text-slate-400">
              {info.componentStack}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
